import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDynamicConfig } from '@statsig/react-bindings';
import { cn } from '@/utils/styles';
import { ShopTestsPage } from './ShopTestsPage';
import { ShopAddOnPage } from './ShopAddOnPage';
import {
  ShopFormulasPage,
  ShopFormulasPageProps,
} from '@screens/ShopPage/ShopFormulasPage';
import { useHasRecommendation } from '@hooks/useHasRecommendation';
import { ShopNoRnrPage } from '@screens/ShopPage/components/ShopNoRnrPage';
import { useGetAllFormulasQuery } from '@viome/viome-base/src/usecase/Shop/ShopQuery';
import { KEYS, LocalStorage } from '@utils/LocalStorage.ts';
import { ErrorLoadingPage } from '@screens/ErrorHandling/ErrorLoadingPage';
import { ShopLandingPageSkeleton } from './ShopLandingPageSkeleton';
import { EventTracker } from '@/utils';
import { KitType } from '@viome/viome-base/src/usecase/Bootup/BootupModel';
import { ShopUnavailablePage } from '@screens/ShopPage/components/ShopUnavailablePage';
import { ShopFormulaRecsDisabledPage } from '@screens/ShopPage/components/ShopFormulaRecsDisabledPage';
import { useShowFormulaRecs } from '@/hooks/useShowFormulaRecs';

type Tab = 'formulas' | 'addon' | 'tests';

const TABS: { id: Tab; label: string }[] = [
  { id: 'formulas', label: 'My Formulas' },
  { id: 'addon', label: 'Add-On' },
  { id: 'tests', label: 'Tests' },
];

const TAB_IDS = TABS.map((tab) => tab.id);

export const ShopLandingPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab') as Tab | null;
  const initialTab = tabFromUrl && TAB_IDS.includes(tabFromUrl) ? tabFromUrl : undefined;

  const publicConfig = useDynamicConfig('public_config');
  const isAddOnEnabled = publicConfig.get('enable_ats', false);

  const showFormulaRecs = useShowFormulaRecs();
  const { data: productData, isLoading, isError } = useGetAllFormulasQuery({
    enabled: showFormulaRecs,
  });

  const localStorageUser = LocalStorage.getItem(KEYS.BOOTUP);
  const { isEligibleForSupplements, kitType, preRnRKitType } =
    localStorageUser && JSON.parse(localStorageUser);

  const isPhproUser =
    kitType === KitType.PHPRO ||
    preRnRKitType === KitType.PHPRO ||
    kitType === KitType.GHPRO ||
    preRnRKitType === KitType.GHPRO;

  const visibleTabs = useMemo(() => {
    if (isPhproUser) {
      // PHPro always sees "My Formulas" regardless of is_eligible_for_supplements
      // (unchanged). For now, Add-on rides along whenever the flag is on —
      // the is_eligible_for_supplements check for this path is still TBD.
      return TABS.filter(
        (tab) => tab.id === 'formulas' || (tab.id === 'addon' && isAddOnEnabled),
      );
    }

    if (!isEligibleForSupplements) {
      return TABS.filter((tab) => tab.id === 'tests');
    }

    return isAddOnEnabled
      ? TABS
      : TABS.filter((tab) => tab.id !== 'addon');
  }, [isPhproUser, isEligibleForSupplements, isAddOnEnabled]);

  const isNoRnrUser = useHasRecommendation();
  const defaultTab = useMemo(() => {
    if (!productData) {
      return undefined;
    }

    if (isPhproUser) {
      return 'formulas';
    }
    
    const allProductsSubscribed =
      productData.length > 0 &&
      productData.every((formula) => formula.is_subscribed);
    return allProductsSubscribed || !isEligibleForSupplements
      ? 'tests'
      : 'formulas';
  }, [productData, isEligibleForSupplements, isPhproUser]);

  const [userSelectedTab, setUserSelectedTab] = useState<Tab | undefined>(
    initialTab,
  );

  const requestedTab = userSelectedTab || defaultTab;

  // Fall back to whichever tab is actually visible when the requested tab
  // (from a deep link, a stale selection, or a disabled feature flag) isn't
  // available for this user — e.g. `?tab=addon` when enable_ats is off, or
  // for a PHPro user who only ever sees the formulas tab.
  const activeTab =
    requestedTab && visibleTabs.some((tab) => tab.id === requestedTab)
      ? requestedTab
      : visibleTabs[0]?.id;

  // Keep the `tab` URL param in sync whenever the requested tab got
  // overridden by the fallback above — otherwise a stale `?tab=addon` (from
  // a share link, a disabled flag, or a PHPro user) stays in the address bar
  // even though a different tab is actually showing.
  useEffect(() => {
    if (!activeTab || searchParams.get('tab') === activeTab) return;

    setSearchParams(
      (params) => {
        const nextParams = new URLSearchParams(params);
        nextParams.set('tab', activeTab);
        return nextParams;
      },
      { replace: true },
    );
  }, [activeTab, searchParams, setSearchParams]);

  const [showTabs, setShowTabs] = useState(true);

  const handleCustomizeModeChange: ShopFormulasPageProps['onCustomizeModeChange'] =
    (isCustomizing) => {
      setShowTabs(!isCustomizing);
    };

  if (isLoading) {
    return <ShopLandingPageSkeleton />;
  }

  if (isError) {
    return (
      <ErrorLoadingPage
        variant="light"
        errorType="error"
        hideBackground
        hideNavBar
      />
    );
  }

  return (
    <div className="page-container mx-auto px-4 py-6" data-testid="shopLanding-page">
      {showTabs && showFormulaRecs && (
        <div className="flex justify-center mb-8" data-testid="shopLanding-tabs">
          <div className="flex rounded-full" data-testid="shopLanding-tabsContainer">
            {(defaultTab === 'tests'
              ? [...visibleTabs].reverse()
              : visibleTabs
            ).map((tab) => (
              <button
                key={tab.id}
                className={cn(
                  'py-2 px-6 rounded-full typography-btn-medium transition-colors',
                  activeTab === tab.id
                    ? 'bg-white text-primary shadow-sm'
                    : 'text-secondary hover:text-primary',
                )}
                onClick={() => {
                  EventTracker.trackEvent(
                    'shop',
                    `tap_shop_home_maintab_my${tab.id}`,
                  );
                  setUserSelectedTab(tab?.id);
                  setSearchParams(
                    (params) => {
                      const nextParams = new URLSearchParams(params);
                      nextParams.set('tab', tab.id);
                      return nextParams;
                    },
                    { replace: true },
                  );
                }}
                data-testid={`shopLanding-tab-${tab.id}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {!showFormulaRecs ? (
        <ShopFormulaRecsDisabledPage />
      ) : isPhproUser && !isEligibleForSupplements ? (
        <ShopUnavailablePage tab={activeTab === 'addon' ? 'addon' : 'formulas'} />
      ) : !isEligibleForSupplements || activeTab === 'tests' ? (
        <ShopTestsPage />
      ) : activeTab === 'addon' ? (
        <ShopAddOnPage />
      ) : !isNoRnrUser ? (
        <ShopNoRnrPage />
      ) : (
        <ShopFormulasPage onCustomizeModeChange={handleCustomizeModeChange} />
      )}
    </div>
  );
};